import React from "react";
import FetchHelper from "./lib/fetchHelper";
import { Popover } from "bootstrap";
import { ReactRenderer } from "./lib/ReactRenderer";
import { SWAP_CHANGED_EVENT, possessive, roleName, shiftInSentence, shiftWhen } from "./lib/swapShiftLine";
import CancelExchangeConfirm from "./lib/cancelExchangeConfirm";
import ConfirmDialog from "./lib/confirmDialog";
import { scrollBehavior } from "./lib/motion";
/**
 * When a shift starts - "on 21.10.26 17:00" - as plain text for a dialog's message. No week number
 * and no end time: the accept dialog names two shifts in one sentence, and with both it was too
 * dense to read at a glance.
 */
/**
 * "Mine byttevagter" - the shifts this volunteer has put on the exchange, each with the swap offers
 * waiting on it. The offers they have sent to *other* people are a separate card of their own,
 * MyShiftOffersComponent; the two are opposite ends of the loop.
 *
 * Accepting an offer lapses its competitors server-side, so every action re-fetches rather than
 * patching state - otherwise Accept buttons linger on offers the server has already resolved.
 */
export default class MyShiftExchangesComponent extends React.Component {
    constructor(props) {
        super(props);
        /** The event listener, which must not hand the event object to load() as a callback. */
        this.reload = () => this.load();
        this.load = (onLoaded) => FetchHelper.get(`/bpapi/shiftswap/inbox`)
            // FetchHelper is untyped JavaScript, so the response arrives as {} without this.
            .then((inbox) => {
            const chosen = {};
            inbox.items.forEach((entry) => entry.offers.forEach((offer) => {
                if (offer.candidates.length > 0)
                    chosen[offer.id] = offer.candidates[0].shiftMemberId;
            }));
            this.setState({
                isLoaded: true,
                busy: false,
                memberUdi: inbox.memberUdi,
                items: inbox.items,
                chosen
            }, onLoaded);
        })
            .catch(() => this.setState({ isLoaded: true, busy: false, error: "Kunne ikke hente dine byttevagter" }));
        /**
         * The offer SMS links here with the byttevagt's slug, since a volunteer can easily have several
         * shifts out at once. Degrades to the plain list when the shift has since been taken.
         */
        this.scrollToHighlight = () => {
            if (!this.state.highlight)
                return;
            const elem = document.getElementById(`byttevagt-${this.state.highlight}`);
            if (elem)
                elem.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
        };
        /** Transient feedback anchored on the icon that was clicked, as the exchange controls do. */
        this.popMessage = (anchorId, message) => {
            const elem = document.getElementById(anchorId);
            if (!elem)
                return;
            // The icon's own title attribute becomes the popover's header, so the tip reads as the
            // action followed by its outcome. Passing title: "" to drop the header suppresses the
            // whole popover, so leave it alone.
            const popover = new Popover(elem, { content: message, placement: "top" });
            popover.show();
            // dispose() without a preceding hide(): hiding starts a fade whose transition-end handler
            // reads state that dispose() has already torn down, which throws in Bootstrap's
            // _isWithActiveTrigger. Disposing outright removes the tip and leaves nothing to fire.
            window.setTimeout(() => popover.dispose(), 3000);
        };
        /**
         * The shareable byttevagt link. Written straight to the clipboard rather than shown in a
         * readonly input - the link is only ever copied, never read here, so it does not need a row of
         * its own.
         */
        this.copyLink = (se) => {
            const url = `${window.location.origin}/byttevagt/?slug=${se.slug}`;
            navigator.clipboard.writeText(url)
                .then(() => this.popMessage(`copy-${se.id}`, "Kopieret til clipboard"))
                .catch(() => this.popMessage(`copy-${se.id}`, "Kunne ikke kopiere linket"));
        };
        /** Opens the confirmation; the dialog asks the right question and does the cancelling. */
        this.cancelExchange = (se) => this.setState({ confirmCancel: se });
        this.exchangeCancelled = () => {
            this.setState({ confirmCancel: null });
            this.load();
            // Cancelling lapses the offers on the byttevagt and can take the shift out of this member's
            // own offers, which the other card lists - so tell it to re-fetch.
            window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
        };
        /**
         * Runs an answer to an offer. Its confirmation stays open, with its buttons disabled, until the
         * request settles - closing it on the tap would leave the volunteer looking at an offer that
         * might be about to vanish, with nothing to say whether anything happened.
         */
        this.act = (request, failure) => {
            this.setState({ busy: true, error: null });
            return request
                .then(() => {
                this.setState({ confirmAccept: null, confirmReject: null });
                this.load();
                // Accepting can lapse offers this member has sent elsewhere, which the other card
                // lists, so tell it to re-fetch rather than leave it stale.
                window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
            })
                .catch(() => this.setState({ busy: false, confirmAccept: null, confirmReject: null, error: failure }));
        };
        this.accept = (offer) => this.act(FetchHelper.post(`/bpapi/shiftswap/${offer.id}/accept`, { shiftMemberId: this.state.chosen[offer.id] }), "Byttet kunne ikke gennemføres. Vagten er måske ikke ledig længere.");
        this.reject = (offer, candidate) => this.act(FetchHelper.post(`/bpapi/shiftswap/${offer.id}/reject/${candidate.shiftMemberId}`, {}), "Vagten kunne ikke afvises");
        /**
         * Both answers are final - an accepted swap moves two shifts at once, and a rejection texts the
         * proposer - and both buttons sit a thumb's width apart on a phone, so each asks first. The
         * accept names both shifts, the way Byt nu does on the byttevagt page, because which of the
         * offered shifts is picked is exactly what a mis-tap on the radio list gets wrong.
         */
        this.renderConfirmations = () => {
            const { confirmAccept, confirmReject, busy, chosen } = this.state;
            if (confirmAccept) {
                const { offer, se } = confirmAccept;
                const candidate = offer.candidates.find((c) => c.shiftMemberId === chosen[offer.id]);
                return React.createElement(ConfirmDialog, { heading: "Byt vagter", message: `Du bytter din vagt ${shiftInSentence(se.shift)} med vagten ${shiftInSentence(candidate === null || candidate === void 0 ? void 0 : candidate.shift)} fra ${offer.proposerName}. Byttet kan ikke fortrydes. Er du sikker?`, yesLabel: "Ja, byt", positive: true, busy: busy, onYes: () => this.accept(offer), onNo: () => this.setState({ confirmAccept: null }) });
            }
            if (confirmReject) {
                const { offer, candidate } = confirmReject;
                // The last shift of an offer is the offer: turning it down ends the offer, and says so.
                const last = offer.candidates.length === 1;
                return React.createElement(ConfirmDialog, { heading: last ? "Afvis bytteforslag" : "Afvis vagt", message: last
                        ? `Vil du afvise bytteforslaget fra ${offer.proposerName}? ${offer.proposerName} får besked på sms, og det kan ikke fortrydes.`
                        : `Vil du afvise vagten ${shiftWhen(candidate.shift)} fra ${offer.proposerName}? ${offer.proposerName} får besked på sms, og de andre vagter i forslaget gælder stadig. Det kan ikke fortrydes.`, yesLabel: "Ja, afvis", busy: busy, onYes: () => this.reject(offer, candidate), onNo: () => this.setState({ confirmReject: null }) });
            }
            return null;
        };
        this.renderOffer = (offer, se) => (React.createElement("div", { className: "bi-mb-offer", key: `offer-${offer.id}` },
            React.createElement("div", { className: "bi-mb-offer-from" },
                React.createElement("strong", null, offer.proposerName),
                " tilbyder dig i bytte:"),
            offer.message ? React.createElement("div", { className: "bi-mb-offer-message" }, offer.message) : null,
            React.createElement("div", { className: "bi-mb-picks", role: "radiogroup", "aria-label": `Vælg en af ${possessive(offer.proposerName)} vagter` }, offer.candidates.map((candidate) => {
                const on = this.state.chosen[offer.id] === candidate.shiftMemberId;
                // Each shift carries its own Afvis, which turns down that shift alone: no picking
                // first. Accepting is still the picked one, and the answer at the foot.
                return (React.createElement("div", { className: `bi-mb-pick${on ? " bi-mb-pick-on" : ""}`, key: `cand-${candidate.id}` },
                    React.createElement("button", { type: "button", role: "radio", "aria-checked": on, className: "bi-mb-pick-main", onClick: () => this.setState({
                            chosen: Object.assign(Object.assign({}, this.state.chosen), { [offer.id]: candidate.shiftMemberId })
                        }) },
                        React.createElement("span", { className: "bi-mb-ring" },
                            React.createElement("span", null)),
                        React.createElement("span", { className: "bi-mb-pick-text" },
                            React.createElement("span", { className: "bi-mb-pick-when" }, shiftWhen(candidate.shift)),
                            React.createElement("span", { className: "bi-mb-pick-role" }, roleName(candidate.role)))),
                    React.createElement("button", { type: "button", className: "bi-mb-reject", disabled: this.state.busy, title: "Afvis denne vagt", onClick: () => this.setState({ confirmReject: { offer, se, candidate } }) }, "Afvis")));
            })),
            React.createElement("div", { className: "bi-mb-answer" },
                React.createElement("button", { type: "button", className: "bi-mb-btn bi-mb-btn-accept", disabled: this.state.busy || !this.state.chosen[offer.id], onClick: () => this.setState({ confirmAccept: { offer, se } }) }, "Accepter byttet"),
                React.createElement("button", { type: "button", className: "bi-mb-btn bi-mb-btn-danger", disabled: this.state.busy, onClick: () => this.cancelExchange(se) }, "Tr\u00E6k tilbage"))));
        this.renderInbox = () => {
            if (this.state.items.length === 0) {
                return React.createElement("p", { className: "bi-mb-empty" }, "Du har ingen vagter p\u00E5 bytteb\u00F8rsen lige nu.");
            }
            return this.state.items.map((entry) => {
                var _a;
                const se = entry.shiftExchange;
                const isHighlighted = this.state.highlight === se.slug;
                // One framed block per byttevagt, with its offers inside it, so there is never any doubt
                // which of the volunteer's shifts an offer is for.
                return (React.createElement("article", { className: `bi-mb-card${isHighlighted ? " bi-mb-highlight" : ""}`, id: `byttevagt-${se.slug}`, key: `entry-${se.id}` },
                    React.createElement("div", { className: "bi-mb-head bi-mb-head-own" },
                        React.createElement("span", { className: "bi-mb-head-text" },
                            React.createElement("a", { href: `/vagtinfo?shiftId=${(_a = se.shift) === null || _a === void 0 ? void 0 : _a.id}` }, shiftWhen(se.shift)),
                            React.createElement("span", null,
                                "Din vagt \u00B7 ",
                                roleName(se.role))),
                        React.createElement("button", { type: "button", id: `copy-${se.id}`, className: "bi-mb-icon", "aria-label": "Kopi\u00E9r link til deling", title: "Kopi\u00E9r link til deling", onClick: () => this.copyLink(se) },
                            React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
                                React.createElement("path", { d: "M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" }),
                                React.createElement("path", { d: "M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" }))),
                        React.createElement("button", { type: "button", className: "bi-mb-icon bi-mb-icon-danger", "aria-label": "Tr\u00E6k byttevagten tilbage", title: "Tr\u00E6k byttevagten tilbage", onClick: () => this.cancelExchange(se) },
                            React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
                                React.createElement("path", { d: "M6 6l12 12M18 6 6 18" })))),
                    entry.offers.length > 0
                        ? entry.offers.map((offer) => this.renderOffer(offer, se))
                        : React.createElement("div", { className: "bi-mb-none" }, "Ingen bytteforslag endnu.")));
            });
        };
        this.state = {
            isLoaded: false,
            items: [],
            // Which candidate shift is picked per offer, keyed on offer id. Seeded from the first
            // candidate when the page loads, so Accept is never armed with nothing chosen.
            chosen: {},
            busy: false,
            error: null,
            // The byttevagt whose ✕ was pressed, while its confirmation is open.
            confirmCancel: null,
            // The offer whose Accepter byttet or Afvis was pressed, with its byttevagt, while that
            // confirmation is open.
            confirmAccept: null,
            confirmReject: null,
            // The slug from the SMS deep link, if the volunteer arrived from one.
            highlight: new URLSearchParams(window.location.search).get("slug")
        };
    }
    componentDidMount() {
        // The scroll runs from setState's own callback rather than off load()'s promise: that
        // promise resolves in a microtask, before React has committed the list, so the card the
        // deep link names does not exist yet and the volunteer is left at the top of the page.
        this.load(this.scrollToHighlight);
        window.addEventListener(SWAP_CHANGED_EVENT, this.reload);
    }
    componentWillUnmount() {
        window.removeEventListener(SWAP_CHANGED_EVENT, this.reload);
    }
    render() {
        if (!this.state.isLoaded) {
            return (React.createElement("div", { className: "bp-loading" },
                React.createElement("i", { className: "fa fa-spinner fa-pulse fa-fw" })));
        }
        return (React.createElement("section", { className: "bi-mb-section" },
            React.createElement("div", { className: "bi-mb-section-head" },
                React.createElement("h2", null, "Sat til bytte"),
                React.createElement("span", null, "Vagter, du har sat p\u00E5 bytteb\u00F8rsen, og de bytteforslag du har f\u00E5et.")),
            this.state.error ? React.createElement("div", { className: "bi-bv-alert bi-bv-alert-error", role: "alert" }, this.state.error) : null,
            this.renderInbox(),
            this.state.confirmCancel
                ? React.createElement(CancelExchangeConfirm, { shiftExchangeId: this.state.confirmCancel.id, shiftMemberId: this.state.confirmCancel.shiftMemberId, onCancelled: this.exchangeCancelled, onClose: () => this.setState({ confirmCancel: null }), onError: (message) => this.setState({ confirmCancel: null, error: message }) })
                : null,
            this.renderConfirmations()));
    }
}
ReactRenderer.render("myshiftexchangescomponent", (elem) => React.createElement(MyShiftExchangesComponent, Object.assign({}, (elem.dataset))));
